import { useState } from "react";

type SubmitStatus = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const inquiryTypes = [
  "제품 문의",
  "견적 요청",
  "시공 상담",
  "카탈로그 요청",
  "전시장 방문 예약",
  "기타",
];

const spaces = ["주방", "욕실", "거실·바닥", "외벽·건축", "상업 공간", "기타"];

export default function InquiryForm() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [message, setMessage] = useState("");
  const [contentLength, setContentLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("문의가 정상적으로 접수되었습니다. 담당자가 확인 후 연락드리겠습니다.");
      form.reset();
      setContentLength(0);
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setMessage("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, typeof value === "string" ? value : value.name);
      });

      const response = await fetch("https://readdy.ai/api/form/dahl9j3p14a1h3mr6opg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("문의가 정상적으로 접수되었습니다. 담당자가 확인 후 연락드리겠습니다.");
        form.reset();
        setContentLength(0);
      } else {
        setStatus("error");
        setMessage(
          isSpam
            ? "문의 접수에 실패했습니다. 잠시 후 다시 시도해 주세요."
            : serverMessage || "문의 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요."
        );
      }
    } catch {
      setStatus("error");
      setMessage("네트워크 오류로 접수하지 못했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  const inputClass =
    "h-12 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h2 className="font-heading text-2xl text-foreground-950">문의 접수</h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        아래 내용을 남겨주시면 담당자가 확인 후 순차적으로 연락드립니다. 항목 앞 <span className="text-primary-600">*</span>
        는 필수 입력입니다.
      </p>

      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground-800">
            이름 <span className="text-primary-600">*</span>
          </label>
          <input id="name" name="name" type="text" required placeholder="홍길동" className={`mt-2 ${inputClass}`} />
        </div>
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-foreground-800">
            회사명 / 소속
          </label>
          <input id="company" name="company" type="text" placeholder="선택 입력" className={`mt-2 ${inputClass}`} />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-foreground-800">
            연락처 <span className="text-primary-600">*</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            placeholder="010-0000-0000"
            className={`mt-2 ${inputClass}`}
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
            이메일 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="example@email.com"
            className={`mt-2 ${inputClass}`}
          />
        </div>
        <div>
          <label htmlFor="inquiryType" className="block text-sm font-medium text-foreground-800">
            문의 유형 <span className="text-primary-600">*</span>
          </label>
          <div className="relative mt-2">
            <select
              id="inquiryType"
              name="inquiryType"
              required
              defaultValue="제품 문의"
              className={`${inputClass} cursor-pointer appearance-none pr-10`}
            >
              {inquiryTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </div>
        <div>
          <label htmlFor="space" className="block text-sm font-medium text-foreground-800">
            시공 공간
          </label>
          <div className="relative mt-2">
            <select
              id="space"
              name="space"
              defaultValue="주방"
              className={`${inputClass} cursor-pointer appearance-none pr-10`}
            >
              {spaces.map((space) => (
                <option key={space} value={space}>
                  {space}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="message" className="block text-sm font-medium text-foreground-800">
          문의 내용 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={6}
          onChange={(event) => setContentLength(event.target.value.length)}
          placeholder="원하시는 소재, 공간, 예산, 일정 등을 자유롭게 남겨주세요. (최대 500자)"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <p className="mt-1.5 text-right text-xs text-foreground-500">{contentLength} / 500자</p>
      </div>

      {/* Reserved anti-spam field */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="website-verify-field"
      />

      <label className="mt-5 flex items-start gap-3 text-xs leading-relaxed text-foreground-600">
        <input
          type="checkbox"
          name="privacyAgree"
          value="동의"
          required
          className="mt-0.5 h-4 w-4 cursor-pointer accent-primary-500"
        />
        개인정보 수집 및 이용에 동의합니다. (문의 응대 목적으로만 사용되며, 상담 완료 후 파기됩니다.)
      </label>

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-7 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" /> 접수 중...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" /> 문의 접수하기
          </>
        )}
      </button>

      {status === "success" && (
        <p
          role="status"
          className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900"
        >
          <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-base" />
          {message}
        </p>
      )}
      {status === "error" && (
        <p
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800"
        >
          <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
          {message}
        </p>
      )}
    </form>
  );
}